import React, { useCallback } from 'react';
import classNames from 'classnames';

import Typography, { TypographyVariant } from '../Typography/Typography.js';
import { longSizeToCommonSize } from '../../utils/common.js';

export type AvatarSize = 'small' | 'medium' | 'large';

export interface AvatarProps {
  /**
   * An alt attribute for the rendered img element.
   * Required for accessibility.
   */
  alt: string;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * The size of the avatar.
   * small:   40px x 40px
   * medium:  80px x 80px
   * large:   120px x 120px
   *
   * @default medium
   */
  size?: AvatarSize;

  /**
   * The src attribute for the img element.
   */
  src?: string;

  /**
   * The initials of the person to display.
   * This should be provided if the src is not.
   *
   * @example AA
   */
  initials?: string;
}

const sizeToVariant: Record<AvatarSize, TypographyVariant> = {
  small: 'title-medium',
  medium: 'headline-medium',
  large: 'headline-large',
};

/**
 * Avatars are typically used when you are referencing a provider, typically in association with an appointment.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1862%3A1203
 */
const Avatar = React.forwardRef(
  (
    { alt, className, initials, size = 'medium', src }: AvatarProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const getChild = useCallback(() => {
      if (src) {
        return (
          <img className="ds-avatar__image" src={src} alt={alt} role="img" />
        );
      }

      return (
        <Typography variant={sizeToVariant[size]} bold>
          {initials}
        </Typography>
      );
    }, [initials, size, src]);

    return (
      <div
        className={classNames(
          'ds-avatar',
          `ds-avatar--${longSizeToCommonSize[size]}`,
          className,
        )}
        ref={ref}
      >
        {getChild()}
      </div>
    );
  },
);

Avatar.displayName = 'Avatar';

export default Avatar;
